/**
 * Everything that happened with one household, or at one of its homes, in one place. Priority 6 of the
 * brief.
 *
 * ONE TIMELINE SINCE OPS #84 (`docs/plan-client-workplace.md`): the five grouped lists the history panel
 * drew are one merged list newest first, `Timeline.tsx`, with its filters and its client view, and the
 * history read behind them is retired (phase 2). The panel around the timeline is the history panel's,
 * moved here from `ClientHistory.tsx` unchanged, and a home's page draws it too, its home fixed.
 *
 * TWO SHAPES, ONE LIST. On a desk it is the panel down the right of the record; on a phone it is a
 * disclosure at the foot of it, because this is the widest read in the system and most visits to a
 * client's page on a phone are somebody checking an address.
 *
 * NEITHER SHAPE FETCHES UNTIL IT IS ASKED, and that is not about bytes. The read behind it is step-up
 * gated: the server wants a sign-in fresher than fifteen minutes, and only a fresh prompt stamps one.
 * Fetching it the moment a record opens would meet most desk visits with "sign in again" where the
 * history should be, and would ask a person who came to read an address to re-authenticate through
 * Microsoft. So the panel rests with a press, exactly as the phone's disclosure does, and the gated
 * request is spent by somebody who wanted the history. The one exception is an address that carries
 * the timeline's filters: somebody pasted a filtered timeline to be read, so it opens at once.
 *
 * The money on it asks for a recent sign-in like every other money view, so a stale session is
 * answered with the server's own sign-in link rather than an empty list.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './Field.tsx';
import { SidePanel } from './ListView.tsx';
import { narrowsTheTimeline, Timeline } from './Timeline.tsx';

const HOUSEHOLD_BLURB =
  'Everything that happened with this household, newest first: visits and what changed on them, texts, emails, calls, asks from home, invoices and payments, issues, follow-ups and what the office changed. Narrow it by kind, home or days, or see it as the client saw it.';

const HOME_BLURB =
  "Everything that happened at this home, newest first: its visits and what changed on them, the texts and emails about them, asks from home, invoices and payments, issues and what the office changed. The Household chip adds the household's own rows beside them.";

/**
 * True only where the record actually draws a column for this, which is xl and up. `useWideView`
 * answers the md question every list screen asks, and md is the wrong answer here: the record's split
 * waits for xl, because a fixed 400px panel beside a 248px rail leaves no record at all at md. This
 * file asks its own question so the disclosure covers every width the panel does not, and neither
 * shape is drawn twice.
 */
const BESIDE_THE_RECORD = '(min-width: 80rem)';

function useBesideTheRecord(): boolean {
  const [beside, setBeside] = useState(() => window.matchMedia(BESIDE_THE_RECORD).matches);
  useEffect(() => {
    const query = window.matchMedia(BESIDE_THE_RECORD);
    const listen = () => setBeside(query.matches);
    listen();
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);
  return beside;
}

export function TimelinePanel({
  clientId,
  homeId = null,
}: {
  clientId: string;
  /** The home a home's page fixes the timeline to, or null on the household's page. */
  homeId?: string | null;
}) {
  const beside = useBesideTheRecord();
  const [search] = useSearchParams();
  /** A pasted, filtered timeline opens at once; otherwise the panel rests until it is asked. */
  const [openedByTheAddress] = useState(() => narrowsTheTimeline(search));
  /** Sticky: once somebody has asked, closing the disclosure does not throw the answer away. */
  const [asked, setAsked] = useState(openedByTheAddress);
  const blurb = homeId === null ? HOUSEHOLD_BLURB : HOME_BLURB;

  const timeline = asked ? (
    <div className="mt-3">
      <Timeline clientId={clientId} homeId={homeId} />
    </div>
  ) : null;

  if (beside) {
    return (
      <SidePanel title="History">
        <p className="text-xs leading-4 text-navy/65">{blurb}</p>
        {asked ? (
          timeline
        ) : (
          <div>
            <Button tone="quiet" onClick={() => setAsked(true)}>
              Show the history
            </Button>
            <p className="mt-2 text-xs leading-4 text-navy/65">
              Reading it asks for a sign-in from the last quarter of an hour, the same as the money
              screens do.
            </p>
          </div>
        )}
      </SidePanel>
    );
  }

  return (
    <details
      className="mt-6 rounded-xl border border-navy/12 bg-parchment p-4"
      open={openedByTheAddress}
      onToggle={(event) => {
        if (event.currentTarget.open) setAsked(true);
      }}
    >
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-xl">History</summary>
      <p className="mt-1 text-sm text-navy/70">{blurb}</p>
      {timeline}
    </details>
  );
}
